import React, { lazy, Suspense, useState, useEffect, useContext, useCallback } from 'react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import UserContext, { UserProvider } from './contexts/UserContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { checkEnvironmentVariables } from './utils/envCheck';
import Toast from './components/ui/Toast';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ui/ErrorBoundary';

// Landing pages (static — loaded immediately)
import LandingPage from './dashboard-landing/LandingPage';
import MarketReports from './dashboard-landing/MarketReports';
import UserLogin from './dashboard-landing/UserLogin';
import Compliance from './dashboard-landing/Compliance';
import DataProtection from './dashboard-landing/DataProtection';
import Contact from './dashboard-landing/Contact';
import InvestorResources from './dashboard-landing/InvestorResources';
import Literature from './dashboard-landing/Guides';
import IfaCorporateFinance from './dashboard-landing/IfaCorporateFinance';
import ManagedPortfolioService from './dashboard-landing/ManagedPortfolioService';
import LabuanFoundation from './dashboard-landing/LabuanFoundation';
import BookingPage from './dashboard-landing/BookingPage';
import DemoRegister from './components/DemoRegister';

// Auth & utility components (static)
import EkycPage from './components/EkycPage';
import ClientEkycTermsPage from './components/ClientEkycTermsPage';
import EkycSuccess from './components/EkycSuccess';
import EkycCancel from './components/EkycCancel';
import TacNotification from './components/TacNotification';
import WhatsAppQRWidget from './components/WhatsAppQRWidget';
import ProtectedRoute from './components/ProtectedRoute';
import VerifyEmailChange from './components/VerifyEmailChange';
import EntraSetup from './components/EntraSetup';

// Lazy-loaded dashboard components (code-split per route)
const ClientHome = lazy(() => import('./dashboard-client/ClientHome'));
const ClientRiskProfile = lazy(() => import('./dashboard-client/ClientRiskProfile'));
const ClientDealing = lazy(() => import('./dashboard-client/ClientDealing'));
const ClientProfile = lazy(() => import('./dashboard-client/ClientProfile'));
const ClientPortfolioValuation = lazy(() => import('./dashboard-client/ClientPortfolioValuation'));
const ClientAccountStatement = lazy(() => import('./dashboard-client/ClientAccountStatement'));
const ClientAdvisorDetail = lazy(() => import('./dashboard-client/ClientAdvisorDetail'));
const ClientSupport = lazy(() => import('./dashboard-client/ClientSupport'));
const ClientBank = lazy(() => import('./dashboard-client/ClientBank'));
const ClientApplication = lazy(() => import('./dashboard-client/ClientApplication'));
const ClientGuides = lazy(() => import('./dashboard-client/ClientGuides'));
const ClientLiteratureForms = lazy(() => import('./dashboard-client/ClientLiteratureForms'));
const ClientInvestorResources = lazy(() => import('./dashboard-client/ClientInvestorResources'));
const ClientInstruments = lazy(() => import('./dashboard-client/ClientInstruments'));

const IntroAgentHome = lazy(() => import('./dashboard-introducing-agent/IntroAgentHome'));
const IntroAgentApplication = lazy(() => import('./dashboard-introducing-agent/IntroAgentApplication'));
const IntroAgentClient = lazy(() => import('./dashboard-introducing-agent/IntroAgentClient'));
const IntroAgentDealing = lazy(() => import('./dashboard-introducing-agent/IntroAgentDealing'));
const IntroAgentClientOnboarding = lazy(() => import('./dashboard-introducing-agent/IntroAgentClientOnboarding'));
const IntroAgentProfile = lazy(() => import('./dashboard-introducing-agent/IntroAgentProfile'));
const IntroAgentFormsView = lazy(() => import('./dashboard-introducing-agent/IntroAgentFormsView'));
const IntroAgentSupport = lazy(() => import('./dashboard-introducing-agent/IntroAgentSupport'));
const IntroAgentMessages = lazy(() => import('./dashboard-introducing-agent/IntroAgentMessages'));
const IntroAgentNotifications = lazy(() => import('./dashboard-introducing-agent/IntroAgentNotifications'));
const IntroAgentGuides = lazy(() => import('./dashboard-introducing-agent/IntroAgentGuides'));
const IntroAgentLiteratureForms = lazy(() => import('./dashboard-introducing-agent/IntroAgentLiteratureForms'));
const IntroAgentInvestorResources = lazy(() => import('./dashboard-introducing-agent/IntroAgentInvestorResources'));
const IntroAgentInstruments = lazy(() => import('./dashboard-introducing-agent/IntroAgentInstruments'));

const MarketingCoordinatorHome = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorHome'));
const MarketingCoordinatorIntroducingAgent = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorIntroducingBroker'));
const MarketingCoordinatorIntroAgentDetail = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorIntroAgentDetail'));
const MarketingCoordinatorProfile = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorProfile'));
const MarketingCoordinatorFormsView = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorFormsView'));
const MarketingCoordinatorCompanyOnboarding = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorCompanyOnboarding'));
const MarketingCoordinatorCompanyDetail = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorCompanyDetail'));
const MarketingCoordinatorGuides = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorGuides'));
const MarketingCoordinatorLiteratureForms = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorLiteratureForms'));
const MarketingCoordinatorInvestorResources = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorInvestorResources'));
const MarketingCoordinatorInstruments = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorInstruments'));

const AdminLayout = lazy(() => import('./dashboard-admin/AdminLayout'));
const AdminHome = lazy(() => import('./dashboard-admin/AdminHome'));
const AdminCompany = lazy(() => import('./dashboard-admin/AdminCompany'));
const AdminCompanyDetail = lazy(() => import('./dashboard-admin/AdminCompanyDetail'));
const AdminMarketingCoordinator = lazy(() => import('./dashboard-admin/AdminMarketingCoordinator'));
const AdminInstruments = lazy(() => import('./dashboard-admin/AdminInstruments'));
const AdminUser = lazy(() => import('./dashboard-admin/AdminUser'));
const AdminFormsManager = lazy(() => import('./dashboard-admin/AdminFormsManager'));
const AdminMarketReport = lazy(() => import('./dashboard-admin/AdminMarketReport'));
const AdminProfile = lazy(() => import('./dashboard-admin/AdminProfile'));
const AdminSettings = lazy(() => import('./dashboard-admin/AdminSettings'));
const AdminDemo = lazy(() => import('./dashboard-admin/AdminDemo'));
const AdminAccounts = lazy(() => import('./dashboard-admin/AdminAccounts'));
const AdminNavManagement = lazy(() => import('./dashboard-admin/AdminNavManagement'));
const AdminClientInvestment = lazy(() => import('./dashboard-admin/AdminClientInvestment'));
const AdminTransaction = lazy(() => import('./dashboard-admin/AdminTransaction'));
const AdminClientManagement = lazy(() => import('./dashboard-admin/AdminClientManagement'));
const AdminClientDetail = lazy(() => import('./dashboard-admin/AdminClientDetail'));
const AdminIntroAgentManagement = lazy(() => import('./dashboard-admin/AdminIntroAgentManagement'));
const AdminIntroAgentDetail = lazy(() => import('./dashboard-admin/AdminIntroAgentDetail'));
const AdminBlobStorage = lazy(() => import('./dashboard-admin/AdminBlobStorage'));
const AdminLiterature = lazy(() => import('./dashboard-admin/AdminLiterature'));
const AdminPortfolioConstraints = lazy(() => import('./dashboard-admin/AdminPortfolioConstraints'));
const AdminMPS = lazy(() => import('./dashboard-admin/AdminMPS'));
const AdminNotifications = lazy(() => import('./dashboard-admin/AdminNotifications'));
const ClientNotifications = lazy(() => import('./dashboard-client/ClientNotifications'));
const MarketingCoordinatorNotifications = lazy(() => import('./dashboard-marketing-coordinator/MarketingCoordinatorNotifications'));

// Finance Ops dashboard (new — Phase 2 of admin split)
const FinanceOpsLayout = lazy(() => import('./dashboard-finance-ops/FinanceOpsLayout'));
const FinanceOpsHome = lazy(() => import('./dashboard-finance-ops/FinanceOpsHome'));
const FinanceOpsPlaceholder = lazy(() => import('./dashboard-finance-ops/FinanceOpsPlaceholder'));
const LedgerPanel = lazy(() => import('./dashboard-finance-ops/LedgerPanel'));
const EventHistory = lazy(() => import('./dashboard-finance-ops/EventHistory'));
const Entities = lazy(() => import('./dashboard-finance-ops/Entities'));

// Dashboard route map — shared by HomeRedirect and UserLogin.
// superadmin lands on /admin-home unless localStorage.lastDashboard says otherwise
// (see HomeRedirect). finance_ops only has one dashboard.
const DASHBOARD_MAP = {
  superadmin: '/admin-home',
  finance_ops: '/finance-ops-home',
  marketing_coordinator: '/marketing-coordinator-home',
  introducing_agent: '/introducing-agent-home',
  demo_introducing_agent: '/introducing-agent-home',
  client: '/client-home',
};

// =============================================================================
// HomeRedirect — the single entry point after Azure auth redirect.
//
// Security flow (PKCE + JWT, follows OAuth 2.0 for Browser-Based Apps / RFC 9700):
//   1. MsalProvider handles redirect (exchanges auth code via PKCE)
//   2. acquireTokenSilent retrieves cached token (no extra network call)
//   3. Backend validates JWT via JWKS, returns user
//   4. Navigate directly to role-based dashboard
// =============================================================================
const HomeRedirect = () => {
  const { instance, accounts, inProgress } = useMsal();
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [authInProgress, setAuthInProgress] = useState(false);
  const [authError, setAuthError] = useState(null);

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('userRole');

  // Complete auth: get cached token from MSAL, verify with backend
  // Don't navigate directly — set state and let the render cycle handle it
  // (avoids race condition where navigate fires before context update commits)
  const completeAuth = useCallback(async (account) => {
    setAuthInProgress(true);
    setAuthError(null);
    try {
      // Token is already cached by MsalProvider's handleRedirectPromise — this is instant
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
      } catch (silentError) {
        // Token expired or needs re-auth — clear stale state and trigger fresh login
        if (silentError instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...loginRequest, account });
          return; // Page will redirect to Azure
        }
        // Any other silent error (e.g., corrupt cache) — clear everything and retry
        console.warn('Token acquisition failed, clearing stale auth state:', silentError.message);
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userData');
        await instance.clearCache();
        setAuthInProgress(false);
        return; // Re-render will show landing page (clean state)
      }
      const accessToken = tokenResponse.accessToken;
      if (!accessToken) throw new Error('No access token received from Azure.');

      localStorage.setItem('token', accessToken);
      localStorage.setItem('platform', 'platform');

      // Verify user with backend (JWT validated via JWKS)
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${API_BASE_URL}/api/users/details`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        // Backend rejected the token — clear everything to prevent loop
        localStorage.removeItem('token');
        localStorage.removeItem('platform');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userData');
        await instance.clearCache();
        setAuthInProgress(false);
        setAuthError('Sign-in failed. Your account may not be active. Please try again.');
        return;
      }

      const user = await res.json();
      setUser({
        email: user.email,
        name: user.companyName || account.name,
        role: user.role,
        userId: user.userId,
        companyId: user.companyId,
        activeStatus: user.activeStatus,
      });
      localStorage.setItem('userRole', user.role);

      if (!DASHBOARD_MAP[user.role]) {
        throw new Error(`Unknown role: ${user.role}`);
      }
      // Don't navigate here — setAuthInProgress(false) triggers re-render,
      // then the "if (token && role)" check below handles navigation
      // after the user context has been committed
      setAuthInProgress(false);
    } catch (err) {
      console.error('Auth completion error:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userData');
      // Clear MSAL cache automatically to prevent redirect loops
      try { await instance.clearCache(); } catch (_) {}
      setAuthInProgress(false);
      setAuthError(err.message || 'Authentication failed. Please try again.');
    }
  }, [instance, setUser]);

  // Trigger auth when MSAL is ready and accounts are available
  useEffect(() => {
    if (
      inProgress === InteractionStatus.None &&
      accounts.length > 0 &&
      !token &&
      !authInProgress &&
      !authError
    ) {
      completeAuth(accounts[0]);
      return;
    }

    // Stale MSAL state recovery — if #code= is in the URL but MSAL couldn't
    // process it (e.g. state mismatch from a redirect URI change), auto-clear
    // and redirect to login. No manual cache clearing needed.
    if (accounts.length === 0 && !token && !authInProgress && window.location.hash.includes('code=')) {
      instance.clearCache().then(() => {
        window.location.replace('/login');
      });
    }
  }, [inProgress, accounts, token, authInProgress, authError, completeAuth, instance]);

  // 1. Already authenticated — instant redirect to dashboard.
  //    For superadmin, honour lastDashboard preference (may be /finance-ops-home).
  if (token && role) {
    let target = DASHBOARD_MAP[role];
    if (role === 'superadmin') {
      const last = localStorage.getItem('lastDashboard');
      if (last === '/finance-ops-home' || last === '/admin-home') target = last;
    }
    if (target) return <Navigate to={target} replace />;
    // Stale role (e.g. old 'cogent_admin' cached before the rename) — wipe the
    // session and bounce the user back to the public landing on
    // cogentplatform.com. They re-click Login from there and the next auth
    // round-trip pulls the fresh role from the backend.
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('lastDashboard');
    localStorage.removeItem('platform');
    window.location.replace('https://cogentplatform.com/');
    return null;
  }

  // 2. MSAL is processing redirect OR we're completing auth — show loading
  if (inProgress !== InteractionStatus.None || authInProgress) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-sm text-gray-500">Completing sign-in...</p>
        </div>
      </div>
    );
  }

  // 3. Auth failed — show error with retry option
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-8">
          <p className="text-red-600 text-sm mb-4">{authError}</p>
          <button
            onClick={async () => {
              setAuthError(null);
              // Clear MSAL cache so UserLogin doesn't see stale accounts and redirect back (loop)
              await instance.clearCache();
              navigate('/login', { replace: true });
            }}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // 4. No auth in progress, no accounts — show landing page
  return <LandingPage />;
};

const App = () => {
  const [globalToast, setGlobalToast] = useState(null);

  // Check environment variables on app start
  useEffect(() => {
    checkEnvironmentVariables();
  }, []);

  // Listen for global toast events (e.g., from fetchInterceptor.js)
  useEffect(() => {
    const handler = (e) => setGlobalToast(e.detail);
    window.addEventListener('app-toast', handler);
    return () => window.removeEventListener('app-toast', handler);
  }, []);

  return (
    <UserProvider>
      <TranslationProvider>
        {/* Global toast for non-React contexts (fetchInterceptor) */}
        {globalToast && (
          <Toast
            message={globalToast.message}
            type={globalToast.type}
            onDismiss={() => setGlobalToast(null)}
          />
        )}
        {/* WhatsApp QR Widget - Shows on all pages */}
        <WhatsAppQRWidget />
        
        {/* ErrorBoundary + Suspense wrap lazy-loaded dashboard routes */}
        <ErrorBoundary>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner /></div>}>
        <Routes>
          {/* Landing and Public */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/lander" element={<LandingPage />} />
          <Route path="/login" element={<UserLogin />} />
{/* Legacy MFA routes removed - Azure Entra ID handles MFA automatically during login */}
          <Route path="/market-reports" element={<MarketReports />} />
          <Route path="/compliances" element={<Compliance />} />
          <Route path="/privacy-policy" element={<DataProtection />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/investor-resources" element={<InvestorResources />} />
          <Route path="/literature" element={<Literature />} />
          <Route path="/guides" element={<Literature />} />
          <Route path="/ifa-corporate-finance" element={<IfaCorporateFinance />} />
          <Route path="/managed-portfolio-service" element={<ManagedPortfolioService />} />
          <Route path="/labuan-foundation" element={<LabuanFoundation />} />
          <Route path="/verify-email-change" element={<VerifyEmailChange />} />
          <Route path="/ekyc-terms" element={<EkycPage />} />
          <Route path="/client-ekyc-terms" element={<ClientEkycTermsPage />} />
          <Route path="/ekyc-success" element={<EkycSuccess />} />
          <Route path="/ekyc-cancel" element={<EkycCancel />} />
          <Route path="/entra-setup" element={<EntraSetup />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/demo-register" element={<DemoRegister />} />

          {/* Client */}
          <Route path="/client-home" element={<ProtectedRoute role="client"><ClientHome /></ProtectedRoute>} />
          <Route path="/client-onboarding" element={<ProtectedRoute role="client"><ClientApplication /></ProtectedRoute>} />
          <Route path="/client-risk-profile" element={<ProtectedRoute role="client"><ClientRiskProfile /></ProtectedRoute>} />
          <Route path="/client-dealing" element={<ProtectedRoute role="client"><ClientDealing /></ProtectedRoute>} />
          <Route path="/client-profile" element={<ProtectedRoute role="client"><ClientProfile /></ProtectedRoute>} />
          <Route path="/client-portfolio-valuation" element={<ProtectedRoute role="client"><ClientPortfolioValuation /></ProtectedRoute>} />
          <Route path="/client-forms-view" element={<ProtectedRoute role="client"><ClientApplication /></ProtectedRoute>} />
          <Route path="/client-account-statement" element={<ProtectedRoute role="client"><ClientAccountStatement /></ProtectedRoute>} />
          <Route path="/client-advisor-detail" element={<ProtectedRoute role="client"><ClientAdvisorDetail /></ProtectedRoute>} />
          <Route path="/client-support" element={<ProtectedRoute role="client"><ClientSupport /></ProtectedRoute>} />
          <Route path="/client-bank" element={<ProtectedRoute role="client"><ClientBank /></ProtectedRoute>} />
          <Route path="/client-application" element={<ProtectedRoute role="client"><ClientApplication /></ProtectedRoute>} />
          <Route path="/client-guides" element={<ProtectedRoute role="client"><ClientGuides /></ProtectedRoute>} />
          <Route path="/client-literature-forms" element={<ProtectedRoute role="client"><ClientLiteratureForms /></ProtectedRoute>} />
          <Route path="/client-investor-resources" element={<ProtectedRoute role="client"><ClientInvestorResources /></ProtectedRoute>} />
          <Route path="/client-instruments" element={<ProtectedRoute role="client"><ClientInstruments /></ProtectedRoute>} />
          <Route path="/client-notifications" element={<ProtectedRoute role="client"><ClientNotifications /></ProtectedRoute>} />

          {/* Introducing Agent */}
          <Route path="/intro-agent-home" element={<ProtectedRoute role="introducing_agent"><IntroAgentHome /></ProtectedRoute>} />
          <Route path="/introducing-agent-home" element={<ProtectedRoute role="introducing_agent"><IntroAgentHome /></ProtectedRoute>} />
          <Route path="/intro-agent-application" element={<ProtectedRoute role="introducing_agent"><IntroAgentApplication /></ProtectedRoute>} />
          <Route path="/intro-agent-client" element={<ProtectedRoute role="introducing_agent"><IntroAgentClient /></ProtectedRoute>} />
          <Route path="/intro-agent-dealing" element={<ProtectedRoute role="introducing_agent"><IntroAgentDealing /></ProtectedRoute>} />
          <Route path="/intro-agent-approval/onboarding" element={<ProtectedRoute role="introducing_agent"><IntroAgentClientOnboarding /></ProtectedRoute>} />
          <Route path="/intro-agent-forms-view" element={<ProtectedRoute role="introducing_agent"><IntroAgentFormsView /></ProtectedRoute>} />
          <Route path="/intro-agent-messages" element={<ProtectedRoute role="introducing_agent"><IntroAgentMessages /></ProtectedRoute>} />
          <Route path="/intro-agent-guides" element={<ProtectedRoute role="introducing_agent"><IntroAgentGuides /></ProtectedRoute>} />
          <Route path="/intro-agent-literature-forms" element={<ProtectedRoute role="introducing_agent"><IntroAgentLiteratureForms /></ProtectedRoute>} />
          <Route path="/intro-agent-investor-resources" element={<ProtectedRoute role="introducing_agent"><IntroAgentInvestorResources /></ProtectedRoute>} />
          <Route path="/intro-agent-notifications" element={<ProtectedRoute role="introducing_agent"><IntroAgentNotifications /></ProtectedRoute>} />
          <Route path="/intro-agent-profile" element={<ProtectedRoute role="introducing_agent"><IntroAgentProfile /></ProtectedRoute>} />
          <Route path="/intro-agent-support" element={<ProtectedRoute role="introducing_agent"><IntroAgentSupport /></ProtectedRoute>} />
          <Route path="/intro-agent-instruments" element={<ProtectedRoute role="introducing_agent"><IntroAgentInstruments /></ProtectedRoute>} />

          {/* Marketing Coordinator */}
          <Route path="/marketing-coordinator-home" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorHome /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-introducing-agent" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorIntroducingAgent /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-intro-agent-detail/:userId" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorIntroAgentDetail /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-profile" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorProfile /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-forms-view" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorFormsView /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-company-onboarding" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorCompanyOnboarding /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-company-detail/:companyId" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorCompanyDetail /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-guides" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorGuides /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-literature-forms" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorLiteratureForms /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-investor-resources" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorInvestorResources /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-instruments" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorInstruments /></ProtectedRoute>} />
          <Route path="/marketing-coordinator-notifications" element={<ProtectedRoute role="marketing_coordinator"><MarketingCoordinatorNotifications /></ProtectedRoute>} />

          {/* Admin — shared layout: Header/Sidebar/Footer persist across navigation */}
          <Route element={<ProtectedRoute role="superadmin"><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin-home" element={<AdminHome />} />
            <Route path="/admin-company" element={<AdminCompany />} />
            <Route path="/admin-company-detail/:companyId" element={<AdminCompanyDetail />} />
            <Route path="/admin-marketing-coordinator" element={<AdminMarketingCoordinator />} />
            <Route path="/admin-instruments" element={<AdminInstruments />} />
            <Route path="/admin-user" element={<AdminUser />} />
            <Route path="/admin-forms" element={<AdminFormsManager />} />
            <Route path="/admin-market-report" element={<AdminMarketReport />} />
            <Route path="/admin-transaction" element={<AdminTransaction />} />
            <Route path="/admin-profile" element={<AdminProfile />} />
            <Route path="/admin-settings" element={<AdminSettings />} />
            <Route path="/admin-demo" element={<AdminDemo />} />
            <Route path="/admin-accounts" element={<AdminAccounts />} />
            {/* NAV Management moved to Finance Ops: /finance-ops-nav-panel */}
            <Route path="/admin-trading" element={<AdminClientInvestment />} />
            <Route path="/admin-client-investment" element={<AdminClientInvestment />} />
            <Route path="/admin-client-management" element={<AdminClientManagement />} />
            <Route path="/admin-client-detail/:userId" element={<AdminClientDetail />} />
            <Route path="/admin-intro-agent-management" element={<AdminIntroAgentManagement />} />
            <Route path="/admin-intro-agent-detail/:userId" element={<AdminIntroAgentDetail />} />
            <Route path="/admin-blob-storage" element={<AdminBlobStorage />} />
            <Route path="/admin-literature" element={<AdminLiterature />} />
            <Route path="/admin-portfolio-constraints" element={<AdminPortfolioConstraints />} />
            <Route path="/admin-mps" element={<AdminMPS />} />
            <Route path="/admin-notifications" element={<AdminNotifications />} />
          </Route>

          {/* Finance Ops — shared layout; superadmin ⊇ finance_ops via role hierarchy */}
          <Route element={<ProtectedRoute roles={['finance_ops']}><FinanceOpsLayout /></ProtectedRoute>}>
            <Route path="/finance-ops-home" element={<FinanceOpsHome />} />
            {/* Users & Applications — reuse admin components via the shared
                useDashboardPrefix hook so internal navigation stays within
                /finance-ops-* URLs */}
            <Route path="/finance-ops-client-management"    element={<AdminClientManagement />} />
            <Route path="/finance-ops-client-detail/:userId" element={<AdminClientDetail />} />
            <Route path="/finance-ops-intro-agent-management" element={<AdminIntroAgentManagement />} />
            <Route path="/finance-ops-intro-agent-detail/:userId" element={<AdminIntroAgentDetail />} />
            <Route path="/finance-ops-marketing-coordinator" element={<AdminMarketingCoordinator />} />
            <Route path="/finance-ops-company"               element={<AdminCompany />} />
            <Route path="/finance-ops-company-detail/:companyId" element={<AdminCompanyDetail />} />
            <Route path="/finance-ops-nav-panel" element={<AdminNavManagement />} />
            <Route path="/finance-ops-ledger-panel" element={<LedgerPanel />} />
            <Route path="/finance-ops-ledger-entities" element={<Entities />} />
            {/* Legacy — Accounts is now a tab inside /finance-ops-ledger-entities */}
            <Route path="/finance-ops-ledger-accounts" element={<Entities />} />
            <Route path="/finance-ops-ledger-events" element={<EventHistory />} />
            <Route path="/finance-ops-fee-status" element={<FinanceOpsPlaceholder title="Fee Status" description="Month-end checklist — has 9052 + 9045 fired for every active client?" />} />
            <Route path="/finance-ops-transactions" element={<FinanceOpsPlaceholder title="Transaction Audit" description="Read-only Exante transaction activity." />} />
            <Route path="/finance-ops-credit-rating" element={<FinanceOpsPlaceholder title="Credit Rating" />} />
            <Route path="/finance-ops-statements" element={<FinanceOpsPlaceholder title="Client Statements" description="Generate, regenerate, audit." />} />
          </Route>

        </Routes>
        </Suspense>
        </ErrorBoundary>
        <TacNotification />
      </TranslationProvider>
    </UserProvider>
  );
};

export default App;
